/**
 * TODO!!!!
 *
 * Abstract this general implementation into
 * - PopoverDropdownBuilder
 * - Re-use for TagSelect, TagFilterDropdown immediatly
 * - Allow raw access to Popover & DropdownBulder options
 * - Abstract tag selection logic to helper util for re-use
 * - Add option.required to core DropdownBuilder
 */

import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import type { RenderOption } from 'components/DropdownBuilder';
import { Close } from 'components/Icons';
import Label from 'components/Label';
import PopoverDropdownBuilder from 'components/popovers/PopoverDropdownBuilder';
import type { TagOption } from 'factories/tags';
import { buildTagFilterOptions } from 'factories/tags';
import { intersection } from 'lodash-es';
import type { SyntheticEvent } from 'react';
import React, { useCallback, useMemo } from 'react';
import FontAwesome from 'react-fontawesome';
import { Link } from 'react-router-dom';
import type { TagNormalized, TagsDictionary } from 'redux/modules/tags.types';
import type { Tag } from 'types/tag';
import { notNull } from 'utils/typesafe';
import styles from './TagSelect.module.scss';

interface TagSelectProps {
  name?: string;
  disabled: boolean;
  tags: Array<Tag>;
  allTags: Record<number, Tag> | TagsDictionary;
  requiredTagIds: Array<number>;
  className?: string;
  onChange: (ids: Array<number>) => void;
  onOpenChange?: (value: boolean) => void;
  placeholder: string | React.ReactNode; // defaultProp'd;
  selectedTagIds: Array<number>;
  accountId?: number;
  allowChanges?: boolean;
  popoverFixed?: boolean;
  maxHeight?: number;
  /** Exclude archived tags from the list  */
  excludeArchivedTags?: boolean;
  defaultTagIds?: Array<number>;
  closeOnSelect?: boolean;
  closeWhenRequiredTagsAreSelected?: boolean;
  singleTagMode?: boolean;
}

export const TAG_SELECT_ID = 'tag_select_button';

const TagSelect = (props: TagSelectProps) => {
  const {
    accountId,
    allowChanges,
    tags,
    allTags,
    selectedTagIds,
    disabled,
    requiredTagIds,
    onChange,
    popoverFixed,
    maxHeight,
    excludeArchivedTags,
    defaultTagIds,
    onOpenChange,
    closeOnSelect = false,
    closeWhenRequiredTagsAreSelected = false,
    singleTagMode = false,
  } = props;
  const isMissingRequiredTags =
    intersection(selectedTagIds, requiredTagIds).length !== requiredTagIds.length;

  const getOptions = useMemo(() => {
    return buildTagFilterOptions(
      tags,
      selectedTagIds,
      requiredTagIds,
      excludeArchivedTags,
      defaultTagIds
    );
  }, [tags, selectedTagIds, requiredTagIds, excludeArchivedTags, defaultTagIds]);

  const selectedTags = useMemo(() => {
    return selectedTagIds.reduce<Array<Tag | TagNormalized>>((filteredTags, tagId) => {
      const tag = allTags[tagId] || null;
      if (tag !== null && !defaultTagIds?.includes(tagId)) {
        filteredTags.push(tag);
      }
      return filteredTags;
    }, []);
  }, [allTags, defaultTagIds, selectedTagIds]);

  const handleOnChange = useCallback(
    (ids: Array<number>, setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>) => {
      // In single tag mode, keep only child tags (filter out parents)
      if (singleTagMode && ids.length > 0) {
        const childTags = ids.filter(id => allTags[id]?.parent_id != null);
        ids = childTags.length > 0 ? childTags : ids;
      }

      onChange(ids);
      if (singleTagMode) {
        setIsOpen?.(false);
      }
      if (
        closeWhenRequiredTagsAreSelected &&
        requiredTagIds.every(requiredTagId => ids.includes(requiredTagId))
      ) {
        setIsOpen?.(false);
      }
    },
    [allTags, closeWhenRequiredTagsAreSelected, onChange, requiredTagIds, singleTagMode]
  );

  const handleRemoveTag = useCallback(
    (event: SyntheticEvent, tagId: Tag['id']) => {
      event.stopPropagation();
      let newSelectedTags = selectedTags.filter(tag => tag.id !== tagId);
      const parentTagId = allTags[tagId].parent_id;
      const noTagHasTheSameParent = newSelectedTags.every(tag => tag.parent_id !== parentTagId);
      if (noTagHasTheSameParent) {
        newSelectedTags = newSelectedTags.filter(tag => tag.id !== parentTagId);
      }
      onChange(newSelectedTags.map(tag => tag.id));
    },
    [allTags, onChange, selectedTags]
  );

  const renderSelectedTags = useCallback(
    (isOpen: boolean) => {
      const allTagIds = Object.keys(allTags).map(Number);
      const tagsByParentTagId = allTagIds.reduce(
        (map, id) => {
          const tag = allTags[id];
          if (tag.parent_id != null) {
            if (!map[tag.parent_id]) {
              map[tag.parent_id] = [];
            }
            map[tag.parent_id].push(id);
          }
          return map;
        },
        {} as Record<number, Array<number>>
      );

      return selectedTags
        .map(tag => {
          // Only show parent level tags if there are no children selected
          const tagChildIds = tagsByParentTagId[tag.id] ?? [];
          const tagChildrenSelected = !!intersection(tagChildIds, selectedTagIds).length;

          if (tagChildrenSelected) {
            return null;
          }

          const parentTag = (tag.parent_id && allTags[tag.parent_id]) || false;

          return (
            <button
              disabled={disabled}
              type="button"
              key={`tag-display-${+tag.id}`}
              className={classNames(styles.selectedTag, {
                [styles.selectedTagIsArchived]: !tag.active || tag.hasBeenRemoved,
              })}
              tabIndex={isOpen ? -1 : undefined}
            >
              {tag.emoji && (
                <img className={styles.emoji} src={tag.emoji} alt={tag.name} title={tag.name} />
              )}
              {/* // singleSelect */}
              {!!parentTag && <span className={styles.parentTagName}>{parentTag.name}:&nbsp;</span>}
              {tag.name}
              {tag.hasBeenRemoved ? ' (Removed)' : null}
              {!tag.active ? ' (Archived)' : null}
              <Close width={8} height={8} onClick={event => handleRemoveTag(event, tag.id)} />
            </button>
          );
        })
        .filter(notNull);
    },
    [allTags, disabled, handleRemoveTag, selectedTagIds, selectedTags]
  );

  const renderOption: RenderOption<TagOption> = (item, index, meta) => {
    const optionClass = classNames({
      [styles.childOption]: meta.isChild,
      [styles.parentOption]: !meta.isChild,
      [styles.missingRequiredOption]: item.required && !meta.isSelected,
      [styles.archivedTag]: !item.active || item.hasBeenRemoved,
    });

    return (
      <div className={optionClass}>
        <input
          tabIndex={-1}
          type={meta.isChild ? 'radio' : 'checkbox'}
          checked={meta.isSelected}
          readOnly
        />
        {!!item.image && <img alt={item.label} className={styles.emoji} src={item.image} />}
        {!!item.label && item.label}
        <div className={styles.labelContainer}>
          {item.hasBeenRemoved && (
            <Label size="small" variant="muted-danger" className={styles.requiredOptionLabel}>
              Removed
            </Label>
          )}
          {!item.active && (
            <Label size="small" variant="muted-danger" className={styles.requiredOptionLabel}>
              Archived
            </Label>
          )}
          {item.required && (
            <Label
              size="small"
              variant={meta.isSelected ? 'muted' : 'muted-danger'}
              className={styles.requiredOptionLabel}
            >
              Required
            </Label>
          )}
        </div>
      </div>
    );
  };

  const renderTarget = useCallback(
    (targetProps: { isOpen: boolean; onClose?: () => void }) => {
      return (
        <div
          data-testid="logged_tags"
          className={classNames(styles.targetContainer, props.className, {
            [styles.disabledTargetContainer]: disabled,
          })}
          tabIndex={-1}
          onMouseDown={e => e.preventDefault()}
          onTouchEnd={e => {
            // On mobile, FloatingFocusManager's focus trap can prevent the
            // synthesized click event from reaching the reference element,
            // breaking Floating UI's useClick toggle. Handle close explicitly
            // via touchend (which always fires reliably on mobile).
            // preventDefault stops the synthesized click from reopening.
            if (targetProps.isOpen) {
              e.preventDefault();
              targetProps.onClose?.();
            }
          }}
        >
          {selectedTags.length ? (
            renderSelectedTags(targetProps.isOpen)
          ) : (
            <Button
              size="small"
              priority="low"
              id={TAG_SELECT_ID}
              data-testid="tag_select_button"
              // Avoid shift-tab being intercepted
              tabIndex={targetProps.isOpen ? -1 : undefined}
              disabled={disabled}
              className={classNames(styles.targetButton)}
            >
              {props.placeholder}
            </Button>
          )}
          {isMissingRequiredTags && (
            <Label variant="muted-danger" size="small" className={styles.requiredWarningLabel}>
              {selectedTagIds.length ? (
                <FontAwesome name="exclamation-circle" />
              ) : (
                'Missing required tags'
              )}
            </Label>
          )}
          <FontAwesome
            name="caret-down"
            className={classNames(styles.toggleIcon, {
              [styles.toggleIconActive]: targetProps.isOpen,
            })}
          />
        </div>
      );
    },
    [
      disabled,
      isMissingRequiredTags,
      props.className,
      renderSelectedTags,
      selectedTagIds.length,
      selectedTags.length,
      props.placeholder,
    ]
  );

  if (disabled) {
    return renderTarget({ isOpen: false });
  }

  const inputActions =
    allowChanges && accountId != null ? (
      <Link tabIndex={-1} to="/settings/labels">
        Manage tags
      </Link>
    ) : null;

  return (
    <PopoverDropdownBuilder
      onOpenChange={onOpenChange}
      renderOption={renderOption}
      value={null}
      values={selectedTagIds}
      options={getOptions}
      onChange={handleOnChange}
      singleChildSelect={!singleTagMode}
      target={renderTarget}
      inputActions={inputActions}
      highlightSelection={false}
      popoverFixed={popoverFixed}
      maxHeight={maxHeight}
      position="bottom-start"
      rootOptionIsGroup={singleTagMode}
      closeOnChange={singleTagMode ? true : closeOnSelect}
      singleSelect={singleTagMode}
    />
  );
};

TagSelect.defaultProps = {
  placeholder: 'Choose tags',
  disabled: false,
  tags: [],
  requiredTagIds: [],
};

export default TagSelect;
